import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

interface PinGroup {
  color: string;
  name: string | null;
  isVisible: boolean;
}

interface ProjectSettingsProps {
  projectId: number;
  projectName: string;
  onClose: () => void;
  onSave?: () => void;
}

const ProjectSettings: React.FC<ProjectSettingsProps> = ({ projectId, projectName, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState<'pinGroups' | 'dataTools'>('pinGroups');
  // Admin-only tools (visible to Alex directly or while impersonating via the
  // adminEmail marker — same gate as Extract Companies / Prospect Pages).
  const { user } = useAuth();
  const isAdmin = user?.email === 'alex@marketmapr.com' || localStorage.getItem('adminEmail') === 'alex@marketmapr.com';

  // Pin Groups state
  const [pinGroups, setPinGroups] = useState<PinGroup[]>([]);
  const [saving, setSaving] = useState(false);
  const [editingGroup, setEditingGroup] = useState<string | null>(null);

  // Data Tools state
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState<{ current: number; total: number; cacheHits: number; apiCalls: number; phase: number; message?: string } | null>(null);
  const [geocodeResult, setGeocodeResult] = useState<{ processed: number; cacheHits: number; apiCalls: number } | null>(null);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [geocodePending, setGeocodePending] = useState<number | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [connectionsProgress, setConnectionsProgress] = useState<{ phase: string; companiesProcessed?: number; totalCompanies?: number; connectionsFound?: number; candidates?: number; companies?: number; loaded?: number; total?: number; saved?: number } | null>(null);
  const [connectionsResult, setConnectionsResult] = useState<{ total: number; candidates: number; duration: string } | null>(null);
  const [connectionsError, setConnectionsError] = useState<string | null>(null);

  // Re-link candidates ↔ companies state
  const [relinking, setRelinking] = useState(false);
  const [relinkResult, setRelinkResult] = useState<{
    associationsCreated: number;
    primaryCreated: number;
    secondaryCreated: number;
    associationsDeleted?: number;
  } | null>(null);
  const [relinkError, setRelinkError] = useState<string | null>(null);

  // Clear cache tags state (admin-only card)
  const [clearingCache, setClearingCache] = useState(false);
  const [cacheTagCount, setCacheTagCount] = useState<{ candidates: number; companies: number } | null>(null);
  const [clearCacheResult, setClearCacheResult] = useState<{ candidatesCleared: number; companiesCleared: number } | null>(null);
  const [clearCacheError, setClearCacheError] = useState<string | null>(null);

  // Dedupe state
  const [deduping, setDeduping] = useState(false);
  const [dedupePreview, setDedupePreview] = useState<{
    duplicateGroupsFound: number;
    candidatesToRemove: number;
    details: { matchKey: string; totalCopies: number; removingCount: number }[];
  } | null>(null);
  const [dedupeResult, setDedupeResult] = useState<{ duplicateGroupsFound: number; candidatesRemoved: number } | null>(null);
  const [dedupeError, setDedupeError] = useState<string | null>(null);

  // Hiring-manager dedupe state (mirrors the candidate dedupe above)
  const [hmDeduping, setHmDeduping] = useState(false);
  const [hmDedupePreview, setHmDedupePreview] = useState<{
    duplicateGroupsFound: number;
    managersToRemove: number;
    details: { matchKey: string; totalCopies: number; removingCount: number }[];
  } | null>(null);
  const [hmDedupeResult, setHmDedupeResult] = useState<{ duplicateGroupsFound: number; managersRemoved: number } | null>(null);
  const [hmDedupeError, setHmDedupeError] = useState<string | null>(null);

  const isRunning = geocoding || calculating || deduping || relinking || hmDeduping;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    fetchPinGroups();
  }, [projectId]);

  // Admin card: how many rows currently carry the ⚡ Cache marker.
  useEffect(() => {
    if (!isAdmin || activeTab !== 'dataTools') return;
    (async () => {
      try {
        const res = await fetch(`/api/projects/${projectId}/cache-tag-count`, { headers: getAuthHeaders() });
        if (res.ok) setCacheTagCount(await res.json());
      } catch { /* count is cosmetic */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, activeTab, projectId]);

  const handleClearCacheTags = async () => {
    const total = (cacheTagCount?.candidates || 0) + (cacheTagCount?.companies || 0);
    if (!window.confirm(
      `Clear the ⚡ Cache markers from ${total > 0 ? `${total.toLocaleString()} record(s) in` : ''} this project?\n\nThe records and their data stay — only the cache badge is removed.`
    )) return;
    setClearingCache(true);
    setClearCacheError(null);
    setClearCacheResult(null);
    try {
      const res = await fetch(`/api/projects/${projectId}/clear-cache-tags`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to clear cache tags');
      setClearCacheResult(data);
      setCacheTagCount({ candidates: 0, companies: 0 });
    } catch (e: any) {
      setClearCacheError(e.message);
    } finally {
      setClearingCache(false);
    }
  };

  const fetchPinGroups = async () => {
    try {
      const response = await fetch(`/api/pin-groups/project/${projectId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const groups = await response.json();
        setPinGroups(groups);
      }
    } catch (error) {
      console.error('Error fetching pin groups:', error);
    }
  };

  const fetchGeocodePending = async () => {
    try {
      const response = await fetch(`/api/geocoding/candidates/pending-count?project_id=${projectId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setGeocodePending(data.pending_count);
      }
    } catch (error) {
      console.error('Error fetching pending count:', error);
    }
  };

  const handlePinGroupSave = async (color: string, newName: string) => {
    setSaving(true);
    try {
      const response = await fetch(`/api/pin-groups/project/${projectId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          color,
          name: newName.trim() || null,
          isVisible: true
        })
      });

      if (response.ok) {
        setPinGroups(prev => prev.map(g =>
          g.color === color ? { ...g, name: newName.trim() || null } : g
        ));
        setEditingGroup(null);
        onSave?.();
      }
    } catch (error) {
      console.error('Error saving pin group:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleGeocode = async () => {
    setGeocoding(true);
    setGeocodeResult(null);
    setGeocodeProgress(null);
    setGeocodeError(null);
    let completed = false;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/geocoding/candidates/stream?project_id=${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok || !response.body) {
        setGeocodeError('Failed to start geocoding');
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'start') {
                setGeocodeProgress({ current: 0, total: data.total, cacheHits: 0, apiCalls: 0, phase: 1, message: data.message });
              } else if (data.type === 'progress') {
                setGeocodeProgress({ current: data.current, total: data.total, cacheHits: data.cacheHits, apiCalls: data.apiCalls, phase: 1 });
              } else if (data.type === 'phase') {
                setGeocodeProgress(prev => prev ? { ...prev, phase: data.phase, message: data.message } : null);
              } else if (data.type === 'complete') {
                setGeocodeResult({ processed: data.processed || 0, cacheHits: data.cacheHits || 0, apiCalls: data.apiCalls || 0 });
                setGeocodePending(0);
                setGeocodeProgress(null);
                completed = true;
              } else if (data.type === 'error') {
                setGeocodeError(data.message || 'Geocoding failed on server');
                setGeocodeProgress(null);
                completed = true;
              }
            } catch { /* ignore parse errors */ }
          }
        }
      }
      if (!completed) {
        setGeocodeError('Connection lost. The operation may still be running on the server — try refreshing in a minute.');
        setGeocodeProgress(null);
      }
    } catch (error) {
      console.error('Error geocoding candidates:', error);
      setGeocodeError('Connection error. The operation may still be running on the server.');
      setGeocodeProgress(null);
    } finally {
      setGeocoding(false);
    }
  };

  const handleCalculateConnections = async () => {
    setCalculating(true);
    setConnectionsResult(null);
    setConnectionsProgress(null);
    setConnectionsError(null);
    let completed = false;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/connections/calculate/${projectId}/stream`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok || !response.body) {
        setConnectionsError('Failed to start connections calculation');
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'start') {
                setConnectionsProgress({ phase: 'loading', candidates: data.candidates });
              } else if (data.type === 'progress') {
                setConnectionsProgress(data);
              } else if (data.type === 'complete') {
                setConnectionsResult({ total: data.totalConnections ?? 0, candidates: data.candidatesProcessed ?? 0, duration: data.durationSeconds?.toFixed(1) ?? '0' });
                setConnectionsProgress(null);
                completed = true;
                onSave?.();
              } else if (data.type === 'error') {
                setConnectionsError(data.message || 'Calculation failed on server');
                setConnectionsProgress(null);
                completed = true;
              }
            } catch { /* ignore parse errors */ }
          }
        }
      }
      if (!completed) {
        setConnectionsError('Connection lost. The operation may still be running on the server — try refreshing in a minute.');
        setConnectionsProgress(null);
      }
    } catch (error) {
      console.error('Error calculating connections:', error);
      setConnectionsError('Connection error. The operation may still be running on the server.');
      setConnectionsProgress(null);
    } finally {
      setCalculating(false);
    }
  };

  // Re-build the candidate_company_associations junction table from scratch.
  // The "candidate associations" count column on the Companies tab reads from
  // this table — if it's stale (typically because companies were enriched
  // before candidates were added), the count shows 0 even when the side panel
  // shows linked candidates. Calling the reconcile endpoint with rebuild:true
  // wipes and regenerates the project's associations.
  const handleRelinkCandidates = async () => {
    setRelinking(true);
    setRelinkResult(null);
    setRelinkError(null);
    try {
      const res = await fetch(`/api/candidates/${projectId}/reconcile-associations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ rebuild: true })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to re-link candidates');
      }
      const data = await res.json();
      setRelinkResult({
        associationsCreated: data.associationsCreated || 0,
        primaryCreated: data.primaryCreated || 0,
        secondaryCreated: data.secondaryCreated || 0,
        associationsDeleted: data.associationsDeleted || 0
      });
    } catch (err: any) {
      setRelinkError(err.message || 'Failed to re-link candidates');
    } finally {
      setRelinking(false);
    }
  };

  const handleDedupeScan = async () => {
    setDeduping(true);
    setDedupePreview(null);
    setDedupeResult(null);
    setDedupeError(null);
    try {
      const response = await fetch(`/api/candidates/${projectId}/deduplicate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ dryRun: true })
      });
      if (!response.ok) {
        setDedupeError('Failed to scan for duplicates');
        return;
      }
      const data = await response.json();
      if (data.duplicatesFound === 0 || data.duplicateGroupsFound === 0) {
        setDedupePreview({ duplicateGroupsFound: 0, candidatesToRemove: 0, details: [] });
      } else {
        setDedupePreview({
          duplicateGroupsFound: data.duplicateGroupsFound,
          candidatesToRemove: data.candidatesToRemove,
          details: data.details || []
        });
      }
    } catch (error) {
      console.error('Error scanning for duplicates:', error);
      setDedupeError('Error scanning for duplicates');
    } finally {
      setDeduping(false);
    }
  };

  const handleDedupeExecute = async () => {
    setDeduping(true);
    setDedupeError(null);
    try {
      const response = await fetch(`/api/candidates/${projectId}/deduplicate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ dryRun: false })
      });
      if (!response.ok) {
        setDedupeError('Failed to remove duplicates');
        return;
      }
      const data = await response.json();
      setDedupeResult({
        duplicateGroupsFound: data.duplicateGroupsFound,
        candidatesRemoved: data.candidatesRemoved
      });
      setDedupePreview(null);
      onSave?.();
    } catch (error) {
      console.error('Error removing duplicates:', error);
      setDedupeError('Error removing duplicates');
    } finally {
      setDeduping(false);
    }
  };

  const handleHmDedupe = async (dryRun: boolean) => {
    setHmDeduping(true);
    setHmDedupeError(null);
    if (dryRun) { setHmDedupePreview(null); setHmDedupeResult(null); }
    try {
      const response = await fetch(`/api/hiring-managers/${projectId}/deduplicate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ dryRun })
      });
      if (!response.ok) {
        setHmDedupeError(dryRun ? 'Failed to scan for duplicates' : 'Failed to remove duplicates');
        return;
      }
      const data = await response.json();
      if (dryRun) {
        setHmDedupePreview({
          duplicateGroupsFound: data.duplicateGroupsFound || 0,
          managersToRemove: data.managersToRemove || 0,
          details: data.details || []
        });
      } else {
        setHmDedupeResult({
          duplicateGroupsFound: data.duplicateGroupsFound || 0,
          managersRemoved: data.managersRemoved || 0
        });
        setHmDedupePreview(null);
        onSave?.();
      }
    } catch (error) {
      console.error('Error deduplicating hiring managers:', error);
      setHmDedupeError('Error deduplicating hiring managers');
    } finally {
      setHmDeduping(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-dialog max-w-3xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col animate-scale-in">
        {/* Header */}
        <div className="p-6 border-b border-gray-200/80">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Project Settings</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200/80">
          <button
            onClick={() => setActiveTab('pinGroups')}
            className={`px-6 py-3 text-sm font-medium transition-all duration-200 ${
              activeTab === 'pinGroups'
                ? 'tab-active'
                : 'tab-inactive'
            }`}
          >
            Pin Groups
          </button>
          <button
            onClick={() => { setActiveTab('dataTools'); fetchGeocodePending(); }}
            className={`px-6 py-3 text-sm font-medium transition-all duration-200 ${
              activeTab === 'dataTools'
                ? 'tab-active'
                : 'tab-inactive'
            }`}
          >
            Data Tools
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'pinGroups' && (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Name your pin groups to easily identify different markets or regions
              </p>
              {pinGroups.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No pin groups found. Upload some companies first!
                </div>
              ) : (
                <div className="space-y-4">
                  {pinGroups.map((group) => (
                    <div
                      key={group.color}
                      className="flex items-center gap-4 p-4 border border-gray-200/80 rounded-xl hover:border-gray-300 hover:shadow-card transition-all duration-200"
                    >
                      <div
                        className="w-12 h-12 rounded-lg border-2 border-white shadow-md flex-shrink-0"
                        style={{ backgroundColor: group.color }}
                      />
                      <div className="flex-1">
                        {editingGroup === group.color ? (
                          <input
                            type="text"
                            defaultValue={group.name || ''}
                            autoFocus
                            placeholder="e.g., UK Agencies, NZ Market"
                            className="input border-blue-300"
                            onBlur={(e) => handlePinGroupSave(group.color, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handlePinGroupSave(group.color, e.currentTarget.value);
                              }
                              if (e.key === 'Escape') {
                                setEditingGroup(null);
                              }
                            }}
                          />
                        ) : (
                          <div>
                            <div className="text-lg font-semibold text-gray-900">
                              {group.name || 'Unnamed Group'}
                            </div>
                            <div className="text-sm text-gray-500">
                              Color: {group.color}
                            </div>
                          </div>
                        )}
                      </div>
                      {editingGroup !== group.color && (
                        <button
                          onClick={() => setEditingGroup(group.color)}
                          className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-200"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'dataTools' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                Maintenance tools for this project's data. Use these to fix missing geocodes, recalculate connections, or remove duplicates.
              </p>

              {/* Clear cache tags — admin only (incl. impersonation) */}
              {isAdmin && (
                <div className="p-5 bg-amber-50/50 rounded-xl border border-amber-200/80">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-lg">⚡</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 tracking-tight flex items-center gap-2">
                        Clear Cache Markers
                        <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Admin</span>
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Remove the ⚡ Cache badge from all candidates and companies in this project. The records and their data are untouched — do this after reviewing what the cache pulled in.
                      </p>
                      {cacheTagCount && !clearCacheResult && (
                        <p className={`text-sm mt-2 font-medium ${(cacheTagCount.candidates + cacheTagCount.companies) > 0 ? 'text-amber-700' : 'text-green-600'}`}>
                          {(cacheTagCount.candidates + cacheTagCount.companies) > 0
                            ? `${cacheTagCount.candidates.toLocaleString()} candidate(s) and ${cacheTagCount.companies.toLocaleString()} company row(s) currently tagged`
                            : 'No cache-tagged records in this project'}
                        </p>
                      )}
                      {clearCacheResult && (
                        <div className="mt-2 p-3 bg-green-50 border border-green-200/80 rounded-xl">
                          <p className="text-sm text-green-800 font-medium">
                            Cleared — {clearCacheResult.candidatesCleared.toLocaleString()} candidate(s) and {clearCacheResult.companiesCleared.toLocaleString()} company row(s) untagged
                          </p>
                        </div>
                      )}
                      {clearCacheError && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200/80 rounded-xl">
                          <p className="text-sm text-red-800 font-medium">{clearCacheError}</p>
                        </div>
                      )}
                      <button
                        onClick={handleClearCacheTags}
                        disabled={clearingCache || (cacheTagCount !== null && cacheTagCount.candidates + cacheTagCount.companies === 0)}
                        className={`mt-3 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                          clearingCache || (cacheTagCount !== null && cacheTagCount.candidates + cacheTagCount.companies === 0)
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-amber-600 text-white hover:bg-amber-700'
                        }`}
                      >
                        {clearingCache ? 'Clearing…' : 'Clear cache markers'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Geocode Candidates */}
              <div className="p-5 bg-gray-50 rounded-xl border border-gray-200/80">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 tracking-tight">Geocode Candidates</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Geocode any candidates that don't have map coordinates yet. Uses a global cache so repeated locations are instant.
                    </p>
                    {!geocoding && geocodePending !== null && geocodePending > 0 && !geocodeResult && (
                      <p className="text-sm text-amber-600 mt-2 font-medium">
                        {geocodePending.toLocaleString()} candidate{geocodePending !== 1 ? 's' : ''} pending geocoding
                      </p>
                    )}
                    {!geocoding && geocodePending === 0 && !geocodeResult && (
                      <p className="text-sm text-green-600 mt-2 font-medium">
                        All candidates are geocoded
                      </p>
                    )}
                    {geocoding && geocodeProgress && (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 font-medium">
                            {geocodeProgress.phase === 1
                              ? `Geocoding addresses: ${geocodeProgress.current}/${geocodeProgress.total}`
                              : 'Updating candidates from cache...'}
                          </span>
                          {geocodeProgress.phase === 1 && geocodeProgress.total > 0 && (
                            <span className="text-gray-500">{Math.round((geocodeProgress.current / geocodeProgress.total) * 100)}%</span>
                          )}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: geocodeProgress.phase === 1 && geocodeProgress.total > 0
                              ? `${(geocodeProgress.current / geocodeProgress.total) * 100}%`
                              : '100%' }}
                          />
                        </div>
                        <p className="text-xs text-gray-500">
                          {geocodeProgress.cacheHits} from cache, {geocodeProgress.apiCalls} API calls
                        </p>
                      </div>
                    )}
                    {geocodeResult && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200/80 rounded-xl">
                        <p className="text-sm text-green-800 font-medium">
                          Geocoding complete — {geocodeResult.processed.toLocaleString()} candidates updated ({geocodeResult.cacheHits} from cache, {geocodeResult.apiCalls} API calls)
                        </p>
                      </div>
                    )}
                    {geocodeError && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200/80 rounded-xl">
                        <p className="text-sm text-red-800 font-medium">{geocodeError}</p>
                      </div>
                    )}
                    <button
                      onClick={handleGeocode}
                      disabled={geocoding || geocodePending === 0}
                      className={`mt-3 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        geocoding || geocodePending === 0
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {geocoding ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                          Geocoding...
                        </span>
                      ) : 'Geocode Candidates'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Recalculate Connections */}
              <div className="p-5 bg-gray-50 rounded-xl border border-gray-200/80">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 tracking-tight">Recalculate Candidate Connections</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Rebuild the candidate-to-candidate connection graph for this project — i.e. who has worked at the same company at the same time as someone else. Useful if connections are missing or candidates have been re-enriched.
                    </p>
                    {calculating && connectionsProgress && (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 font-medium">
                            {connectionsProgress.phase === 'loading' && connectionsProgress.total
                              ? `Loading candidates: ${(connectionsProgress.loaded || 0).toLocaleString()}/${connectionsProgress.total.toLocaleString()}`
                              : connectionsProgress.phase === 'loading' && `Loading ${connectionsProgress.candidates?.toLocaleString()} candidates...`}
                            {connectionsProgress.phase === 'indexing' && `Indexed ${connectionsProgress.companies?.toLocaleString()} companies from ${connectionsProgress.candidates?.toLocaleString()} candidates`}
                            {connectionsProgress.phase === 'calculating' && `Comparing: ${connectionsProgress.companiesProcessed?.toLocaleString()}/${connectionsProgress.totalCompanies?.toLocaleString()} companies`}
                            {connectionsProgress.phase === 'saving' && `Saving: ${(connectionsProgress.saved || 0).toLocaleString()}/${(connectionsProgress.total || 0).toLocaleString()} candidates`}
                          </span>
                          {connectionsProgress.phase === 'loading' && connectionsProgress.total && connectionsProgress.total > 0 && (
                            <span className="text-gray-500">{Math.round(((connectionsProgress.loaded || 0) / connectionsProgress.total) * 100)}%</span>
                          )}
                          {connectionsProgress.phase === 'calculating' && connectionsProgress.totalCompanies && connectionsProgress.totalCompanies > 0 && (
                            <span className="text-gray-500">{Math.round(((connectionsProgress.companiesProcessed || 0) / connectionsProgress.totalCompanies) * 100)}%</span>
                          )}
                          {connectionsProgress.phase === 'saving' && connectionsProgress.total && connectionsProgress.total > 0 && (
                            <span className="text-gray-500">{Math.round(((connectionsProgress.saved || 0) / connectionsProgress.total) * 100)}%</span>
                          )}
                        </div>
                        {(connectionsProgress.phase === 'loading' || connectionsProgress.phase === 'calculating' || connectionsProgress.phase === 'saving') && (() => {
                          let pct = 0;
                          if (connectionsProgress.phase === 'loading' && connectionsProgress.total)
                            pct = ((connectionsProgress.loaded || 0) / connectionsProgress.total) * 100;
                          else if (connectionsProgress.phase === 'calculating' && connectionsProgress.totalCompanies)
                            pct = ((connectionsProgress.companiesProcessed || 0) / connectionsProgress.totalCompanies) * 100;
                          else if (connectionsProgress.phase === 'saving' && connectionsProgress.total)
                            pct = ((connectionsProgress.saved || 0) / connectionsProgress.total) * 100;
                          return (
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          );
                        })()}
                        {connectionsProgress.connectionsFound !== undefined && (
                          <p className="text-xs text-gray-500">
                            {connectionsProgress.connectionsFound.toLocaleString()} connections found so far
                          </p>
                        )}
                      </div>
                    )}
                    {connectionsResult && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200/80 rounded-xl">
                        <p className="text-sm text-blue-800 font-medium">
                          Complete — {connectionsResult.total.toLocaleString()} connection{connectionsResult.total !== 1 ? 's' : ''} across {connectionsResult.candidates.toLocaleString()} candidates ({connectionsResult.duration}s)
                        </p>
                      </div>
                    )}
                    {connectionsError && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200/80 rounded-xl">
                        <p className="text-sm text-red-800 font-medium">{connectionsError}</p>
                      </div>
                    )}
                    <button
                      onClick={handleCalculateConnections}
                      disabled={calculating}
                      className={`mt-3 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        calculating
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {calculating ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                          Calculating...
                        </span>
                      ) : 'Recalculate Connections'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Re-link Candidates to Companies */}
              <div className="p-5 bg-gray-50 rounded-xl border border-gray-200/80">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 tracking-tight">Re-link Candidates to Companies</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Rebuilds the candidate ↔ company links used by the <em>Candidate associations</em> count on the Companies tab. Run this if the count looks wrong (e.g. shows 0 but the company side panel lists candidates) — usually after enriching companies before adding candidates.
                    </p>
                    {relinkResult && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200/80 rounded-xl">
                        <p className="text-sm text-blue-800 font-medium">
                          Done — created {relinkResult.associationsCreated.toLocaleString()} link
                          {relinkResult.associationsCreated !== 1 ? 's' : ''} ({relinkResult.primaryCreated.toLocaleString()} primary, {relinkResult.secondaryCreated.toLocaleString()} from position history){relinkResult.associationsDeleted ? `, replacing ${relinkResult.associationsDeleted.toLocaleString()} stale` : ''}.
                        </p>
                      </div>
                    )}
                    {relinkError && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200/80 rounded-xl">
                        <p className="text-sm text-red-800 font-medium">{relinkError}</p>
                      </div>
                    )}
                    <button
                      onClick={handleRelinkCandidates}
                      disabled={relinking || isRunning}
                      className={`mt-3 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        relinking || isRunning
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-amber-600 text-white hover:bg-amber-700'
                      }`}
                    >
                      {relinking ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                          Re-linking...
                        </span>
                      ) : 'Re-link Candidates to Companies'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Deduplicate Candidates */}
              <div className="p-5 bg-gray-50 rounded-xl border border-gray-200/80">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 tracking-tight">Deduplicate Candidates</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Find and remove duplicate candidates based on matching LinkedIn profile URLs. Keeps the oldest record for each duplicate group.
                    </p>

                    {/* Scan preview results */}
                    {dedupePreview && dedupePreview.candidatesToRemove === 0 && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200/80 rounded-xl">
                        <p className="text-sm text-green-800 font-medium">No duplicates found — all candidates are unique.</p>
                      </div>
                    )}
                    {dedupePreview && dedupePreview.candidatesToRemove > 0 && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200/80 rounded-xl space-y-2">
                        <p className="text-sm text-amber-800 font-medium">
                          Found {dedupePreview.duplicateGroupsFound} duplicate group{dedupePreview.duplicateGroupsFound !== 1 ? 's' : ''} — {dedupePreview.candidatesToRemove} candidate{dedupePreview.candidatesToRemove !== 1 ? 's' : ''} to remove
                        </p>
                        {dedupePreview.details.length > 0 && dedupePreview.details.length <= 10 && (
                          <ul className="text-xs text-amber-700 space-y-0.5 max-h-32 overflow-y-auto">
                            {dedupePreview.details.map((d, i) => (
                              <li key={i} className="truncate" title={d.matchKey}>
                                {d.matchKey} — {d.totalCopies} copies, removing {d.removingCount}
                              </li>
                            ))}
                          </ul>
                        )}
                        {dedupePreview.details.length > 10 && (
                          <p className="text-xs text-amber-700">
                            Showing summary only ({dedupePreview.details.length} groups)
                          </p>
                        )}
                        <button
                          onClick={handleDedupeExecute}
                          disabled={deduping}
                          className="btn-danger text-sm"
                        >
                          {deduping ? (
                            <span className="flex items-center gap-2">
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                              Removing...
                            </span>
                          ) : `Remove ${dedupePreview.candidatesToRemove} Duplicate${dedupePreview.candidatesToRemove !== 1 ? 's' : ''}`}
                        </button>
                      </div>
                    )}

                    {/* Completion result */}
                    {dedupeResult && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200/80 rounded-xl">
                        <p className="text-sm text-green-800 font-medium">
                          Done — removed {dedupeResult.candidatesRemoved} duplicate{dedupeResult.candidatesRemoved !== 1 ? 's' : ''} from {dedupeResult.duplicateGroupsFound} group{dedupeResult.duplicateGroupsFound !== 1 ? 's' : ''}
                        </p>
                      </div>
                    )}

                    {/* Error */}
                    {dedupeError && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200/80 rounded-xl">
                        <p className="text-sm text-red-800 font-medium">{dedupeError}</p>
                      </div>
                    )}

                    <button
                      onClick={handleDedupeScan}
                      disabled={isRunning}
                      className={`mt-3 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        isRunning
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                      }`}
                    >
                      {deduping && !dedupePreview ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                          Scanning...
                        </span>
                      ) : 'Scan for Duplicates'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Deduplicate Hiring Managers */}
              <div className="p-5 bg-gray-50 rounded-xl border border-gray-200/80">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0 .656.126 1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 tracking-tight">Deduplicate Hiring Managers</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Find and remove duplicate hiring managers by LinkedIn URL (or name + company where no URL exists). Keeps the oldest record for each duplicate group.
                    </p>

                    {hmDedupePreview && hmDedupePreview.managersToRemove === 0 && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200/80 rounded-xl">
                        <p className="text-sm text-green-800 font-medium">No duplicates found — all hiring managers are unique.</p>
                      </div>
                    )}
                    {hmDedupePreview && hmDedupePreview.managersToRemove > 0 && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200/80 rounded-xl space-y-2">
                        <p className="text-sm text-amber-800 font-medium">
                          Found {hmDedupePreview.duplicateGroupsFound} duplicate group{hmDedupePreview.duplicateGroupsFound !== 1 ? 's' : ''} — {hmDedupePreview.managersToRemove} hiring manager{hmDedupePreview.managersToRemove !== 1 ? 's' : ''} to remove
                        </p>
                        {hmDedupePreview.details.length > 0 && hmDedupePreview.details.length <= 10 && (
                          <ul className="text-xs text-amber-700 space-y-0.5 max-h-32 overflow-y-auto">
                            {hmDedupePreview.details.map((d, i) => (
                              <li key={i} className="truncate" title={d.matchKey}>
                                {d.matchKey} — {d.totalCopies} copies, removing {d.removingCount}
                              </li>
                            ))}
                          </ul>
                        )}
                        {hmDedupePreview.details.length > 10 && (
                          <p className="text-xs text-amber-700">
                            Showing summary only ({hmDedupePreview.details.length} groups)
                          </p>
                        )}
                        <button
                          onClick={() => handleHmDedupe(false)}
                          disabled={hmDeduping}
                          className="btn-danger text-sm"
                        >
                          {hmDeduping ? (
                            <span className="flex items-center gap-2">
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                              Removing...
                            </span>
                          ) : `Remove ${hmDedupePreview.managersToRemove} Duplicate${hmDedupePreview.managersToRemove !== 1 ? 's' : ''}`}
                        </button>
                      </div>
                    )}

                    {hmDedupeResult && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200/80 rounded-xl">
                        <p className="text-sm text-green-800 font-medium">
                          Done — removed {hmDedupeResult.managersRemoved} duplicate{hmDedupeResult.managersRemoved !== 1 ? 's' : ''} from {hmDedupeResult.duplicateGroupsFound} group{hmDedupeResult.duplicateGroupsFound !== 1 ? 's' : ''}
                        </p>
                      </div>
                    )}

                    {hmDedupeError && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200/80 rounded-xl">
                        <p className="text-sm text-red-800 font-medium">{hmDedupeError}</p>
                      </div>
                    )}

                    <button
                      onClick={() => handleHmDedupe(true)}
                      disabled={isRunning}
                      className={`mt-3 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        isRunning
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                      }`}
                    >
                      {hmDeduping && !hmDedupePreview ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                          Scanning...
                        </span>
                      ) : 'Scan for Duplicates'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200/80 bg-gray-50 rounded-b-2xl">
          {isRunning && (
            <p className="text-xs text-gray-500 text-center mb-2">
              Operations run on the server — closing this dialog won't cancel them.
            </p>
          )}
          <button
            onClick={onClose}
            className="w-full btn-primary"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectSettings;
